import type { Partner } from "@expert/shared-types";
import { useQuery } from "@tanstack/react-query";
import { getProductInfo } from "../api";
import { useContentfulClient } from "../ContentfulClientProvider";
import type { Language, ProductInfo } from "../types";

function filterForRelevantSkus(
    skuMap: Record<string, ProductInfo>,
    productSkuArray: string[],
): Record<string, ProductInfo> {
    if (!productSkuArray || productSkuArray.length === 0) {
        return skuMap;
    }
    return Object.fromEntries(Object.entries(skuMap).filter(([key]) => productSkuArray.includes(key)));
}

export const useProductInfo = (
    partner: Partner | "verizonCare",
    employeeId: string | null,
    productSkuArray?: string[],
    language: Language = "en-US",
) => {
    const contentfulClient = useContentfulClient();
    // the filtering is done outside of the api call because react-query will perform caching for us
    // we want to cache all the product info  from contentful and then filter the response to the caller
    // rather than caching the filtered response which will force us to fetch multiple times
    const { data, error, isLoading } = useQuery({
        queryKey: [`product-info-${partner}-${language}`],
        queryFn: async () => await getProductInfo(partner, employeeId, contentfulClient, language),
        refetchOnWindowFocus: false,
        staleTime: 1000 * 60 * 5, //5 mins
        select: (apiData) => {
            const allProducts = apiData.products || {};
            const filteredProducts = productSkuArray
                ? filterForRelevantSkus(allProducts, productSkuArray)
                : allProducts;

            return {
                filteredProducts,
                displayCues: apiData.displayCues || [],
            };
        },
    });

    return {
        productInfo: data?.filteredProducts,
        displayCues: data?.displayCues || [],
        error,
        isLoading,
    };
};
